import { useQuery } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { useErrorHandler } from '../../hooks/useErrorHandler'
import { usePageTitle } from '../../hooks/usePageTitle'
import { Contract } from '../../models/Contract'
import { RecordFactory } from '../../models/RecordFactory'
import { useHistoryTrackerContext } from '../../providers/HistoryTrackerProvider'
import { useOnboardingContext } from '../../providers/OnboardingProvider'
import { okResult } from '../../types/utils'
import { getEditionID } from '../../utils/users'
import { PSApplication } from '../app/PSApplication'
import { ContractsPage } from '../items/ContractsPage'
import { WorkflowSelectListSkeleton } from '../common/skeletons/WorkflowSelectListSkeleton'
import { useOnboardingBackHandler } from './useOnboardingBackHandler'

type OnboardingContract = {
    contract: Contract
    selected: boolean
}

const mergeContracts = (defaults: Contract[], saved: Contract[]): OnboardingContract[] => {
    return defaults.map((d) => {
        const foundFromSaved = saved.find((c) => c.editionContract === d.id)
        if (foundFromSaved) {
            return { contract: foundFromSaved, selected: true }
        }
        return { contract: d, selected: false }
    })
}

export function SetupOnboardingContractsPage() {
    const user = PSApplication.me()
    const editionID = getEditionID(user)
    const [searchParams] = useSearchParams()
    const { logAndDisplayError } = useErrorHandler()
    const navigate = useNavigate()

    usePageTitle('Contracts')
    const onBack = useOnboardingBackHandler()

    const { data, isLoading } = useQuery({
        queryKey: ['onboarding', 'contracts', editionID],
        queryFn: async () => {
            const [existingContracts, edition] = await Promise.all([
                RecordFactory.getInstance().loadContracts(),
                RecordFactory.getInstance().loadEdition(editionID),
            ])

            const recordIDs = searchParams.get('recordIDs')?.split(',') ?? []

            const contracts = recordIDs.length
                ? (edition?.defaultContracts ?? [])
                      .filter((c) => recordIDs.includes(c.id ?? '') || !c.associatedWithService)
                      .map((c) => ({ contract: c, selected: true }))
                : mergeContracts(edition?.defaultContracts ?? [], existingContracts)

            return {
                contracts,
                edition,
            }
        },
    })

    const { advanceOnboardingState } = useOnboardingContext()

    const industryTitle = user.industry?.name
        ? `${user.industry.name.toLowerCase()}s`
        : 'your industry'

    const onNextHandler = async (contracts: Contract[]) => {
        const clonedContracts = contracts.map((c) => {
            const cClone = c.clone()
            cClone.edition = editionID
            cClone.editionContract = c.id
            cClone.id = undefined
            return cClone
        })

        const result = await RecordFactory.getInstance().sendOnboardingContracts(
            clonedContracts,
            editionID,
            true
        )

        if (!result.ok) {
            logAndDisplayError(result.error)
            return result
        }

        await advanceOnboardingState()
        return okResult(null)
    }

    if (isLoading || !data) {
        return (
            <WorkflowSelectListSkeleton
                title="Contracts"
                subtitle={`These contracts are recommended for ${industryTitle}. You can preview and select
                any you wish from the list below. Add custom contracts later.`}
                onClose={() => {}}
            />
        )
    }

    return (
        <ContractsPage
            onClose={() => navigate('/')}
            contracts={data.contracts}
            onSave={onNextHandler}
            onBack={onBack}
        />
    )
}
